import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import axios, { AxiosError } from 'axios';
import produce, { current } from 'immer';
import Video, { PreflightTestReport } from 'twilio-video';
import UAParser from 'ua-parser-js';
import usePreflightTest from './usePreflightTest/usePreflightTest';
import useTwilioStatus from './useTwilioStatus/useTwilioStatus';
import useBitrateTest from './useBitrateTest/useBitrateTest';
import useSTUNTest from './useSTUNTest/useSTUNTest';
import useICETest from './useICETest/useICETest';
import { VideoInputTest, MediaConnectionBitrateTest, AudioInputTest, AudioOutputTest } from '@twilio/rtc-diagnostics';
import { getQualityScore, calculateRFactor, calculateMOS, calculateIdd, calculateIeEff, sanitizePacketLossStats } from '../panes/Quality/getQualityScore/getQualityScore';
import { STUNAnalysis, CLOUDTALK_STUN_SERVERS } from '../../utils/stunTesting';
import { ICETestResult } from '../../utils/iceCandidatePairTesting';
import { saveTestResult } from '../../utils/testHistory';
import { LocationInfo, lookupBothLocations, inferTwilioEdge } from '../../utils/ipGeolocation';

export enum ActivePane {
  GetStarted,
  DeviceCheck,
  DeviceError,
  CameraTest,
  AudioTest,
  BrowserTest,
  Connectivity,
  Quality,
  Results,
}

export type TwilioAPIStatus = 'operational' | 'major_outage' | 'partial_outage' | 'degraded_performance';

export interface TwilioStatus {
  ['Group Rooms']?: TwilioAPIStatus;
  ['Go Rooms']?: TwilioAPIStatus;
  ['Peer-to-Peer Rooms']?: TwilioAPIStatus;
  ['Recordings']?: TwilioAPIStatus;
  ['Compositions']?: TwilioAPIStatus;
  ['Network Traversal Service']?: TwilioAPIStatus;
}

interface stateType {
  activePane: ActivePane;
  videoGranted: boolean;
  audioGranted: boolean;
  deviceError: null | Error;
  customerEmail: string;
  selectedCallSimulationDuration: number | null;
  durationSelectionError: string | null;
  preflightTest: {
    progress: string | null;
    error: null | Error;
    report: null | PreflightTestReport;
    tokenError: null | Error;
    signalingGatewayReachable: boolean;
    turnServersReachable: boolean;
  };
  stunTest: {
    inProgress: boolean;
    finished: boolean;
    progress: string | null;
    analysis: STUNAnalysis | null;
    error: null | Error;
  };
  iceTest: {
    inProgress: boolean;
    finished: boolean;
    progress: string | null;
    result: ICETestResult | null;
    error: null | Error;
  };
  twilioStatus: TwilioStatus | null;
  twilioStatusError: null | Error;
  videoInputTestReport: null | VideoInputTest.Report;
  audioInputTestReport: null | AudioInputTest.Report;
  audioOutputTestReport: null | AudioOutputTest.Report;
  downButtonDisabled: boolean;
  preflightTestInProgress: boolean;
  preflightTestFinished: boolean;
  bitrateTest: {
    bitrate: null | number;
    report: null | MediaConnectionBitrateTest.Report;
    error: null | Error;
  };
  bitrateTestInProgress: boolean;
  bitrateTestFinished: boolean;
  bitrateTestProgress: number;
  bitrateTestCancelled: boolean;
  qualityMetrics: {
    avgBitrate: null | number;
    bitrateVariation: null | number;
    stabilityScore: null | number;
    overallAverage: null | number;
    testDuration: null | number;
  };
  currentBitrate: null | number;
  appIsExpired: boolean;
  qualityResult: null | ReturnType<typeof getQualityScore>;
  locationInfo: LocationInfo | null;
  locationLookupInProgress: boolean;
}

export type ACTIONTYPE =
  | { type: 'set-active-pane'; newActivePane: ActivePane }
  | { type: 'next-pane' }
  | { type: 'previous-pane' }
  | { type: 'set-devices'; devices: MediaDeviceInfo[] }
  | { type: 'set-device-error'; error: Error }
  | { type: 'set-customer-email'; email: string }
  | { type: 'set-call-simulation-duration'; duration: number }
  | { type: 'set-duration-selection-error'; error: string | null }
  | { type: 'set-video-test-report'; report: VideoInputTest.Report }
  | { type: 'set-audio-input-test-report'; report: AudioInputTest.Report }
  | { type: 'set-audio-output-test-report'; report: AudioOutputTest.Report }
  | { type: 'preflight-progress'; progress: string }
  | { type: 'preflight-completed'; report: PreflightTestReport }
  | { type: 'preflight-failed'; error: Error }
  | { type: 'preflight-token-failed'; error: Error }
  | { type: 'stun-test-started' }
  | { type: 'stun-test-progress'; progress: string }
  | { type: 'stun-test-completed'; analysis: STUNAnalysis }
  | { type: 'stun-test-failed'; error: Error }
  | { type: 'ice-test-started' }
  | { type: 'ice-test-progress'; progress: string }
  | { type: 'ice-test-completed'; result: ICETestResult }
  | { type: 'ice-test-failed'; error: Error }
  | { type: 'set-twilio-status'; statusObj: TwilioStatus }
  | { type: 'set-twilio-status-error'; error: Error }
  | { type: 'preflight-started' }
  | { type: 'preflight-finished' }
  | { type: 'set-bitrate'; bitrate: number }
  | { type: 'set-bitrate-test-error'; error: Error }
  | { type: 'set-bitrate-test-report'; report: MediaConnectionBitrateTest.Report }
  | { type: 'bitrate-test-started' }
  | { type: 'bitrate-test-finished' }
  | { type: 'set-bitrate-test-progress'; progress: number }
  | {
      type: 'update-quality-metrics';
      metrics: {
        avgBitrate: number;
        bitrateVariation: number;
        stabilityScore: number;
        overallAverage?: number;
        testDuration?: number;
      };
    }
  | { type: 'set-current-bitrate'; bitrate: number }
  | { type: 'set-app-is-expired' }
  | { type: 'set-quality-result'; result: ReturnType<typeof getQualityScore> }
  | { type: 'bitrate-test-cancelled' }
  | { type: 'location-lookup-started' }
  | { type: 'location-lookup-completed'; locationInfo: LocationInfo }
  | { type: 'location-lookup-failed'; error: string };

type AppStateContextType = {
  state: stateType;
  dispatch: React.Dispatch<ACTIONTYPE>;
  nextPane: () => void;
  cancelConnectivityTests: () => void;
  startSTUNTest: () => void;
  startICETest: () => void;
  userAgentInfo: UAParser.IResult;
  downloadFinalTestResults: () => void;
};

export const initialState = {
  activePane: ActivePane.GetStarted,
  videoGranted: false,
  audioGranted: false,
  deviceError: null,
  customerEmail: '',
  selectedCallSimulationDuration: 600000, // Default to 10 minutes
  durationSelectionError: null,
  preflightTest: {
    progress: null,
    report: null,
    error: null,
    tokenError: null,
    signalingGatewayReachable: false,
    turnServersReachable: false,
  },
  stunTest: {
    inProgress: false,
    finished: false,
    progress: null,
    analysis: null,
    error: null,
  },
  iceTest: {
    inProgress: false,
    finished: false,
    progress: null,
    result: null,
    error: null,
  },
  twilioStatus: null,
  twilioStatusError: null,
  videoInputTestReport: null,
  audioInputTestReport: null,
  audioOutputTestReport: null,
  downButtonDisabled: false,
  preflightTestInProgress: false,
  preflightTestFinished: false,
  bitrateTest: {
    bitrate: null,
    report: null,
    error: null,
  },
  bitrateTestInProgress: false,
  bitrateTestFinished: false,
  bitrateTestProgress: 0,
  bitrateTestCancelled: false,
  qualityMetrics: {
    avgBitrate: null,
    bitrateVariation: null,
    stabilityScore: null,
    overallAverage: null,
    testDuration: null,
  },
  currentBitrate: null,
  appIsExpired: false,
  qualityResult: null,
  locationInfo: null,
  locationLookupInProgress: false,
};

export const AppStateContext = createContext<AppStateContextType>(null!);

export function useAppStateContext() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppStateContext must be used within an AppStateProvider');
  }
  return context;
}

// helper function for determining whether to disable the down arrow button:
export const isDownButtonDisabled = (currentState: stateType) => {
  const {
    activePane,
    preflightTestInProgress,
    preflightTest,
    bitrateTestInProgress,
    audioInputTestReport,
    audioOutputTestReport,
    videoInputTestReport,
    appIsExpired,
    selectedCallSimulationDuration,
    stunTest,
    iceTest,
  } = currentState;

  const connectionFailedOrLoading =
    activePane === ActivePane.Connectivity &&
    (preflightTestInProgress ||
      bitrateTestInProgress ||
      stunTest.inProgress ||
      iceTest.inProgress ||
      preflightTest.error !== null ||
      preflightTest.tokenError !== null);

  const deviceTestErrors = !!audioInputTestReport?.errors.length || !!audioOutputTestReport?.errors.length;
  // Removed video test errors since we're skipping CameraTest

  const onDeviceCheck = activePane === ActivePane.DeviceCheck || activePane === ActivePane.DeviceError;
  const unsupportedBrowser = activePane === ActivePane.BrowserTest && !Video.isSupported;
  const onGetStartedWithoutDuration = activePane === ActivePane.GetStarted && selectedCallSimulationDuration === null;

  return (
    appIsExpired ||
    connectionFailedOrLoading ||
    !ActivePane[activePane + 1] ||
    deviceTestErrors ||
    onDeviceCheck ||
    unsupportedBrowser ||
    onGetStartedWithoutDuration
  );
};

/**
 * This reducer function helps with the logic used to decide the activePane,
 * as well as which panes are to be shown to the user, and when.
 */

export const appStateReducer = produce((draft: stateType, action: ACTIONTYPE) => {
  switch (action.type) {
    case 'set-active-pane':
      draft.activePane = action.newActivePane;
      break;

    case 'next-pane':
      switch (draft.activePane) {
        case ActivePane.GetStarted:
          if (draft.audioGranted && draft.videoGranted) {
            draft.activePane = ActivePane.AudioTest; // Skip CameraTest, go directly to AudioTest
          } else {
            draft.activePane = ActivePane.DeviceCheck;
          }
          break;
        case ActivePane.DeviceCheck:
          if (draft.deviceError) {
            draft.activePane = ActivePane.DeviceError;
          } else {
            draft.activePane = ActivePane.AudioTest; // Skip CameraTest, go directly to AudioTest
          }
          break;
        case ActivePane.CameraTest:
          // Skip CameraTest by going directly to AudioTest
          draft.activePane = ActivePane.AudioTest;
          break;
        default:
          draft.activePane++;
          break;
      }
      break;

    case 'previous-pane':
      switch (draft.activePane) {
        case ActivePane.CameraTest:
          if (draft.audioGranted && draft.videoGranted) {
            draft.activePane = ActivePane.GetStarted;
          } else {
            draft.activePane = ActivePane.DeviceCheck;
          }
          break;
        case ActivePane.AudioTest:
          // Skip CameraTest when going back, go to DeviceCheck or GetStarted
          if (draft.audioGranted && draft.videoGranted) {
            draft.activePane = ActivePane.GetStarted;
          } else {
            draft.activePane = ActivePane.DeviceCheck;
          }
          break;
        default:
          draft.activePane--;
          break;
      }
      break;

    case 'set-devices':
      draft.audioGranted = action.devices.filter((d) => d.kind === 'audioinput').every((d) => d.label);
      draft.videoGranted = action.devices.filter((d) => d.kind === 'videoinput').every((d) => d.label);
      break;

    case 'set-device-error':
      draft.deviceError = action.error;
      draft.activePane = ActivePane.DeviceError;
      break;

    case 'preflight-progress':
      draft.preflightTest.progress = action.progress;

      // 'connected' means we reached the Twilio signaling server and obtained TURN credentials.
      // Also accept dtlsConnected / peerConnectionConnected as stronger proof (the original signals).
      if (
        action.progress === 'connected' ||
        action.progress === 'dtlsConnected' ||
        action.progress === 'peerConnectionConnected'
      ) {
        draft.preflightTest.signalingGatewayReachable = true;
      }

      // 'iceConnected' means ICE negotiation succeeded through the TURN relay
      // (the preflight test uses iceTransportPolicy: 'relay', so this proves TURN works).
      // Also accept 'mediaSubscribed' as it implies the full TURN path is working.
      if (
        action.progress === 'iceConnected' ||
        action.progress === 'mediaSubscribed'
      ) {
        draft.preflightTest.turnServersReachable = true;
      }
      break;

    case 'preflight-completed':
      draft.preflightTest.report = action.report;
      draft.preflightTest.progress = null;
      break;

    case 'preflight-failed':
      draft.preflightTest.error = action.error;
      draft.preflightTest.progress = null;
      break;

    case 'preflight-token-failed':
      draft.preflightTest.tokenError = action.error;
      break;

    case 'stun-test-started':
      draft.stunTest.inProgress = true;
      draft.stunTest.finished = false;
      draft.stunTest.error = null;
      draft.stunTest.analysis = null;
      break;

    case 'stun-test-progress':
      draft.stunTest.progress = action.progress;
      break;

    case 'stun-test-completed':
      draft.stunTest.inProgress = false;
      draft.stunTest.finished = true;
      draft.stunTest.analysis = action.analysis;
      draft.stunTest.progress = null;
      break;

    case 'stun-test-failed':
      draft.stunTest.inProgress = false;
      draft.stunTest.finished = true;
      draft.stunTest.error = action.error;
      draft.stunTest.progress = null;
      break;

    case 'ice-test-started':
      draft.iceTest.inProgress = true;
      draft.iceTest.finished = false;
      draft.iceTest.error = null;
      draft.iceTest.result = null;
      break;

    case 'ice-test-progress':
      draft.iceTest.progress = action.progress;
      break;

    case 'ice-test-completed':
      draft.iceTest.inProgress = false;
      draft.iceTest.finished = true;
      draft.iceTest.result = action.result;
      draft.iceTest.progress = null;
      break;

    case 'ice-test-failed':
      draft.iceTest.inProgress = false;
      draft.iceTest.finished = true;
      draft.iceTest.error = action.error;
      draft.iceTest.progress = null;
      break;

    case 'set-twilio-status':
      draft.twilioStatus = action.statusObj;
      break;

    case 'set-twilio-status-error':
      draft.twilioStatusError = action.error;
      break;

    case 'set-video-test-report':
      draft.videoInputTestReport = action.report;
      break;

    case 'set-audio-input-test-report':
      draft.audioInputTestReport = action.report;
      break;

    case 'set-audio-output-test-report':
      draft.audioOutputTestReport = action.report;
      break;

    case 'preflight-started':
      draft.preflightTestInProgress = true;
      draft.preflightTestFinished = false;
      break;

    case 'preflight-finished':
      draft.preflightTestFinished = true;
      draft.preflightTestInProgress = false;
      break;

    case 'set-bitrate':
      draft.bitrateTest.bitrate = action.bitrate;
      break;

    case 'set-bitrate-test-report':
      draft.bitrateTest.report = action.report;
      break;

    case 'set-bitrate-test-error':
      draft.bitrateTest.error = action.error;
      break;

    case 'bitrate-test-started':
      draft.bitrateTestInProgress = true;
      draft.bitrateTestFinished = false;
      draft.bitrateTestCancelled = false;
      draft.bitrateTestProgress = 0; // Reset progress at the start of a new test
      break;

    case 'bitrate-test-finished':
      draft.bitrateTestFinished = true;
      draft.bitrateTestInProgress = false;
      // Don't reset bitrateTestProgress here — leave it at the last value
      // so the UI can show "Cancelled at X%" or "Completed at 100%"
      break;

    case 'set-bitrate-test-progress':
      draft.bitrateTestProgress = action.progress;
      break;

    case 'update-quality-metrics':
      draft.qualityMetrics = {
        ...draft.qualityMetrics,
        ...action.metrics,
      };
      break;

    case 'set-current-bitrate':
      draft.currentBitrate = action.bitrate;
      break;

    case 'set-customer-email':
      draft.customerEmail = action.email;
      break;

    case 'set-call-simulation-duration':
      draft.selectedCallSimulationDuration = action.duration;
      draft.durationSelectionError = null; // Clear error when duration is selected
      break;

    case 'set-duration-selection-error':
      draft.durationSelectionError = action.error;
      break;

    case 'set-app-is-expired':
      draft.appIsExpired = true;
      break;

    case 'set-quality-result':
      draft.qualityResult = action.result as any;
      break;

    case 'bitrate-test-cancelled':
      draft.bitrateTestCancelled = true;
      break;

    case 'location-lookup-started':
      draft.locationLookupInProgress = true;
      break;

    case 'location-lookup-completed':
      draft.locationInfo = action.locationInfo;
      draft.locationLookupInProgress = false;
      break;

    case 'location-lookup-failed':
      draft.locationLookupInProgress = false;
      break;
  }

  const currentState = current(draft);

  draft.downButtonDisabled = isDownButtonDisabled(currentState);
});

export const AppStateProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState);
  const { startPreflightTest, cancelPreflightTest } = usePreflightTest(dispatch);
  const { startBitrateTest, cancelBitrateTest } = useBitrateTest(dispatch, state.selectedCallSimulationDuration || undefined);
  const { getTwilioStatus } = useTwilioStatus(dispatch);
  const { startSTUNTest, cancelSTUNTest } = useSTUNTest(dispatch);
  const { startICETest, cancelICETest } = useICETest(dispatch);

  const userAgentParser = new UAParser();
  const userAgentInfo = userAgentParser.getResult();

  const downloadFinalTestResults = () => {
    const signalingGateway = state.preflightTest.signalingGatewayReachable ? 'Reachable' : 'Unreachable';
    const turnServers = state.preflightTest.turnServersReachable ? 'Reachable' : 'Unreachable';
    const maxBitrate = state.bitrateTest.report?.values ? Math.max(...state.bitrateTest.report.values) : 0;

    // Use pre-computed quality result from state (computed once via useEffect)
    const qualityMetrics = state.qualityResult ?? getQualityScore(state.preflightTest.report, state.bitrateTest.report);

    // Build executive summary
    const overallResult =
      qualityMetrics.totalQualityScore >= 2 && // Good or Excellent
      state.preflightTest.signalingGatewayReachable &&
      state.preflightTest.turnServersReachable &&
      !state.stunTest.error &&
      !state.iceTest.error
        ? 'PASS'
        : qualityMetrics.totalQualityScore === 1 // Suboptimal
          ? 'WARNING'
          : 'FAIL';

    const findings: string[] = [];
    if (!state.preflightTest.signalingGatewayReachable) findings.push('Signaling gateway unreachable');
    if (!state.preflightTest.turnServersReachable) findings.push('TURN servers unreachable');
    if (state.stunTest.analysis?.requiresTurn) findings.push(`NAT type (${state.stunTest.analysis.natType}) requires TURN`);
    if (state.stunTest.error) findings.push(`STUN test error: ${state.stunTest.error.message}`);
    if (state.iceTest.error) findings.push(`ICE test error: ${state.iceTest.error.message}`);
    if (qualityMetrics.totalQualityScore <= 1) findings.push(`Quality score: ${['Poor', 'Suboptimal'][qualityMetrics.totalQualityScore]}`);

    // REPORT enhancement: Raw vs. sanitized packet loss
    const rawPacketLoss = state.preflightTest.report?.stats?.packetLoss ?? null;
    const testDurationMs = state.preflightTest.report?.testTiming?.duration ?? 15000;
    const sanitizedPL = rawPacketLoss ? sanitizePacketLossStats(rawPacketLoss, testDurationMs) : null;
    const packetLossWasSanitized = rawPacketLoss && sanitizedPL
      ? (rawPacketLoss.average !== sanitizedPL.average || rawPacketLoss.max !== sanitizedPL.max)
      : false;

    // REPORT enhancement: MOS calculation breakdown
    // Jitter: Twilio SDK returns seconds (WebRTC RFC 3550), convert to ms
    const rttAvg = state.preflightTest.report?.stats?.rtt?.average;
    const jitterAvgRaw = state.preflightTest.report?.stats?.jitter?.average;
    const jitterAvgMs = jitterAvgRaw != null ? jitterAvgRaw * 1000 : undefined;
    const plAvg = sanitizedPL?.average ?? rawPacketLoss?.average;
    const oneWayDelay = rttAvg != null ? rttAvg / 2 : undefined;
    const ta = rttAvg != null && jitterAvgMs != null ? rttAvg / 2 + 2 * jitterAvgMs : undefined;
    const mosBreakdown = ta != null && plAvg != null ? {
      rFactor: calculateRFactor(rttAvg, jitterAvgMs, plAvg),
      delayImpairment_Idd: calculateIdd(ta),
      packetLossImpairment_IeEff: calculateIeEff(plAvg),
      oneWayDelay: oneWayDelay,
      effectiveDelay_Ta: ta,
      mosScore: calculateMOS(rttAvg, jitterAvgMs, plAvg),
    } : null;

    // REPORT enhancement: Bitrate time series with timestamps
    const bitrateTimeSeries = state.bitrateTest.report?.values?.map((v: number, i: number) => ({
      second: i + 1,
      kbps: v,
    })) ?? null;

    // REPORT enhancement: Network connection info (if available)
    const nav = navigator as any;
    const networkInfo = nav.connection ? {
      type: nav.connection.type ?? null,
      downlink: nav.connection.downlink ?? null,
      rtt: nav.connection.rtt ?? null,
      effectiveType: nav.connection.effectiveType ?? null,
    } : null;

    // REPORT enhancement: Error details helper
    const formatError = (error: Error | null) => {
      if (!error) return null;
      return {
        message: error.message,
        name: error.name,
        stack: error.stack ? error.stack.split('\n').slice(0, 5).join('\n') : null,
      };
    };

    const finalTestResults = {
      reportMetadata: {
        timestamp: new Date().toISOString(),
        diagnosticVersion: '1.3.0',
        customerEmail: state.customerEmail || null,
        selectedTestDuration: state.selectedCallSimulationDuration,
        actualBitrateSamples: state.bitrateTest.report?.values?.length || 0,
      },
      summary: {
        overallResult,
        qualityScore: ['Poor', 'Suboptimal', 'Good', 'Excellent'][qualityMetrics.totalQualityScore],
        mosScore: qualityMetrics.mos.formattedScore,
        findings: findings.length > 0 ? findings : ['All tests passed'],
      },

      // REPORT enhancement: Test configuration
      testConfiguration: {
        preflightDuration: 15000,
        bitrateTestDuration: state.selectedCallSimulationDuration,
        stunServers: CLOUDTALK_STUN_SERVERS,
        stunServerCount: CLOUDTALK_STUN_SERVERS.length,
        bitrateTestCancelled: state.bitrateTestCancelled,
        bitrateCompletionPercent: state.bitrateTestProgress,
      },

      // REPORT enhancement: Test completion metadata
      testCompletion: {
        preflightTestComplete: state.preflightTestFinished,
        bitrateTestComplete: state.bitrateTestFinished && !state.bitrateTestCancelled,
        bitrateTestCancelledAt: state.bitrateTestCancelled ? state.bitrateTestProgress : null,
        stunTestComplete: state.stunTest.finished,
        iceTestComplete: state.iceTest.finished,
      },

      // REPORT enhancement: Network connection type
      networkInfo,

      // REPORT enhancement: IP geolocation and network location
      locationInfo: state.locationInfo || null,

      audioTestResults: { inputTest: state.audioInputTestReport, outputTest: state.audioOutputTestReport },
      bitrateTestResults: {
        maxBitrate,
        ...state.bitrateTest.report,
        bitrateTimeSeries,  // REPORT enhancement: timestamped values
      },
      browserInformation: userAgentInfo,
      connectivityResults: {
        twilioServices: { ...state.twilioStatus },
        signalingRegion: signalingGateway,
        TURN: turnServers,
        stunAnalysis: state.stunTest.analysis,
        stunTestError: formatError(state.stunTest.error),
        stunTestCompleted: state.stunTest.finished,
        iceTestResult: state.iceTest.result,
        iceTestError: formatError(state.iceTest.error),
        iceTestCompleted: state.iceTest.finished,
        natFirewallCompatibility: state.stunTest.analysis ? {
          cloudtalkCompatible: state.stunTest.analysis.cloudtalkCompatibility,
          natType: state.stunTest.analysis.natType,
          requiresTurn: state.stunTest.analysis.requiresTurn,
          stunServersReachable: `${state.stunTest.analysis.serversReachable}/${state.stunTest.analysis.totalServers}`,
          externalIP: state.stunTest.analysis.externalIP,
          portMappingConsistent: state.stunTest.analysis.portMappingConsistent,
          individualServerResults: state.stunTest.analysis.results.map(result => ({
            server: result.server,
            success: result.success,
            responseTime: result.responseTime,
            externalIP: result.externalIP,
            externalPort: result.externalPort,
            error: result.error
          }))
        } : state.stunTest.error ? {
          cloudtalkCompatible: 'error',
          natType: 'unknown',
          requiresTurn: true,
          testError: state.stunTest.error.message,
          stunServersReachable: '0/6'
        } : {
          cloudtalkCompatible: 'not-tested',
          natType: 'unknown',
          requiresTurn: null,
          stunServersReachable: 'test-not-completed'
        },
      },

      // REPORT enhancement: Network timing (surfaced prominently)
      networkTiming: (state.preflightTest.report as any)?.networkTiming ?? null,

      // REPORT enhancement: Preflight progress timeline
      preflightProgressTimeline: (state.preflightTest.report as any)?.progressEvents ?? null,

      stunTestResults: {
        testCompleted: state.stunTest.finished,
        testInProgress: state.stunTest.inProgress,
        lastProgress: state.stunTest.progress,
        testError: formatError(state.stunTest.error),
        fullAnalysis: state.stunTest.analysis,
        timestamp: new Date().toISOString()
      },
      iceTestResults: {
        testCompleted: state.iceTest.finished,
        testInProgress: state.iceTest.inProgress,
        lastProgress: state.iceTest.progress,
        testError: formatError(state.iceTest.error),
        connectionEstablished: state.iceTest.result?.success || false,
        selectedCandidatePair: state.iceTest.result?.selectedPair ? {
          localCandidateType: state.iceTest.result.selectedPair.localCandidate.type,
          remoteCandidateType: state.iceTest.result.selectedPair.remoteCandidate.type,
          connectionType: state.iceTest.result.selectedPair.connectionType,
          roundTripTime: state.iceTest.result.selectedPair.currentRoundTripTime,
          nominated: state.iceTest.result.selectedPair.nominated,
          state: state.iceTest.result.selectedPair.state,
          localAddress: state.iceTest.result.selectedPair.localCandidate.address,
          remoteAddress: state.iceTest.result.selectedPair.remoteCandidate.address,
          protocol: state.iceTest.result.selectedPair.localCandidate.protocol
        } : null,
        connectionTiming: state.iceTest.result ? {
          connectionEstablishmentTime: state.iceTest.result.connectionEstablishmentTime,
          iceGatheringTime: state.iceTest.result.iceGatheringTime,
          iceConnectionState: state.iceTest.result.iceConnectionState,
          iceGatheringState: state.iceTest.result.iceGatheringState
        } : null,
        fullResult: state.iceTest.result,
        timestamp: new Date().toISOString()
      },
      preflightTestReport: {
        report: state.preflightTest.report,
        error: formatError(state.preflightTest.error),
      },
      qualityAnalysis: {
        latency: qualityMetrics.latency,
        jitter: qualityMetrics.jitter,
        // REPORT enhancement: Raw + sanitized packet loss
        packetLoss: qualityMetrics.packetLoss,
        rawPacketLoss: rawPacketLoss ? {
          average: rawPacketLoss.average,
          max: rawPacketLoss.max,
          min: rawPacketLoss.min,
        } : null,
        packetLossSanitized: packetLossWasSanitized,
        packetLossSanitizationNote: packetLossWasSanitized
          ? 'Packet loss stats were adjusted to account for ICE establishment overhead (first ~2 seconds of the test have no media flow, which registers as packet loss).'
          : null,
        bitrate: qualityMetrics.bitrate,
        mos: qualityMetrics.mos,
        // REPORT enhancement: MOS calculation breakdown
        mosBreakdown,
        overallQualityScore: qualityMetrics.totalQualityScore,
        // REPORT enhancement: Quality score thresholds used
        thresholds: {
          rtt: { good: 150, suboptimal: 300, poor: 400 },
          packetLoss: { good: 1, suboptimal: 3, poor: 5 },
          jitter: { good: 30, suboptimal: 50, poor: 100 },
          bitrate: { good: 1000, suboptimal: 500, poor: 150 },
          mos: { excellent: 4.0, good: 3.5, suboptimal: 2.5 },
        },
      },
      videoTestResults: null, // Video test skipped
    };

    // Save to localStorage history (Fix #14)
    const qualityScoreLabel = ['Poor', 'Suboptimal', 'Good', 'Excellent'][qualityMetrics.totalQualityScore];
    saveTestResult({
      timestamp: new Date().toISOString(),
      overallResult: overallResult as 'PASS' | 'FAIL' | 'WARNING',
      mosScore: qualityMetrics.mos.formattedScore,
      qualityScore: qualityScoreLabel,
      latencyAvg: qualityMetrics.latency.average,
      packetLossAvg: qualityMetrics.packetLoss.average,
      bitrateAvg: qualityMetrics.bitrate.average,
      natType: state.stunTest.analysis?.natType ?? null,
      turnReachable: state.preflightTest.turnServersReachable,
    });

    const link = document.createElement('a');
    link.download = 'test_results.json';
    link.href = URL.createObjectURL(
      new Blob([JSON.stringify(finalTestResults, null, 2)], {
        type: 'application/json',
      })
    );
    link.click();
  };

  // Compute quality result once when preflight and bitrate test reports are both available.
  // This deduplicates the getQualityScore() call that was previously in Quality.tsx, Results.tsx, and downloadFinalTestResults().
  useEffect(() => {
    if (state.preflightTest.report || state.bitrateTest.report) {
      const result = getQualityScore(state.preflightTest.report, state.bitrateTest.report);
      dispatch({ type: 'set-quality-result', result });
    }
  }, [state.preflightTest.report, state.bitrateTest.report]);

  // Trigger IP geolocation lookup when STUN test provides the external IP.
  // The server IP comes from the PREFLIGHT test's selected ICE candidate pair
  // (the actual Twilio media server, NOT the local ICE test which is peer-to-peer).
  // Runs as a fire-and-forget async operation — never blocks the test flow.
  useEffect(() => {
    const clientIP = state.stunTest.analysis?.externalIP;
    if (!clientIP || state.locationInfo || state.locationLookupInProgress) return;

    // Get Twilio server IP from preflight report (may be null if preflight hasn't finished yet)
    const serverIP = state.preflightTest.report?.selectedIceCandidatePairStats?.remoteCandidate?.address ?? null;

    dispatch({ type: 'location-lookup-started' });

    lookupBothLocations(clientIP, serverIP)
      .then((locationInfo) => {
        dispatch({ type: 'location-lookup-completed', locationInfo });
      })
      .catch((error) => {
        console.warn('Geolocation lookup failed:', error);
        dispatch({ type: 'location-lookup-failed', error: String(error) });
      });
  }, [
    state.stunTest.analysis?.externalIP,
    state.preflightTest.report?.selectedIceCandidatePairStats?.remoteCandidate?.address,
    state.locationInfo,
    state.locationLookupInProgress,
  ]);

  // Re-lookup server location if preflight completes after initial geo lookup
  // (initial lookup had null server IP because preflight wasn't done yet)
  useEffect(() => {
    const serverIP = state.preflightTest.report?.selectedIceCandidatePairStats?.remoteCandidate?.address;
    if (
      state.locationInfo &&
      !state.locationInfo.server?.lat &&
      serverIP &&
      !state.locationLookupInProgress
    ) {
      const clientIP = state.locationInfo.client?.ip || '';
      dispatch({ type: 'location-lookup-started' });

      lookupBothLocations(clientIP, serverIP)
        .then((locationInfo) => {
          dispatch({ type: 'location-lookup-completed', locationInfo });
        })
        .catch(() => {
          dispatch({ type: 'location-lookup-failed', error: 'Server IP re-lookup failed' });
        });
    }
  }, [state.preflightTest.report?.selectedIceCandidatePairStats?.remoteCandidate?.address, state.locationInfo, state.locationLookupInProgress]);

  const nextPane = useCallback(() => {
    switch (state.activePane) {
      case ActivePane.GetStarted: {
        startBitrateTest();
        startPreflightTest();

        // Guard STUN/ICE tests behind a WebRTC availability check.
        // Without RTCPeerConnection the tests would throw cryptic errors.
        const webRTCAvailable = typeof RTCPeerConnection !== 'undefined';
        if (webRTCAvailable) {
          startSTUNTest();
          // Start ICE test after STUN test completes to avoid interference
          setTimeout(() => startICETest(), 2000);
        } else {
          const err = new Error('WebRTC (RTCPeerConnection) is not available in this browser');
          dispatch({ type: 'stun-test-failed', error: err });
          dispatch({ type: 'ice-test-failed', error: err });
        }

        getTwilioStatus();
        dispatch({ type: 'next-pane' });
        break;
      }
      default:
        dispatch({ type: 'next-pane' });
    }
  }, [startBitrateTest, startPreflightTest, getTwilioStatus, state, dispatch]);

  const cancelConnectivityTests = useCallback(() => {
    cancelBitrateTest();
    cancelPreflightTest();
    cancelSTUNTest();
    cancelICETest();
  }, [cancelBitrateTest, cancelPreflightTest, cancelSTUNTest, cancelICETest]);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => dispatch({ type: 'set-devices', devices }));

    // Initial token check
    axios('app/token').catch((error: AxiosError) => {
      if (error.response?.data?.error?.message === 'token server expired') {
        dispatch({ type: 'set-app-is-expired' });
      }
    });

    // Periodic token check every 60 seconds to detect expiry during long tests
    const tokenCheckInterval = setInterval(() => {
      axios('app/token').catch((error: AxiosError) => {
        if (error.response?.data?.error?.message === 'token server expired') {
          dispatch({ type: 'set-app-is-expired' });
          clearInterval(tokenCheckInterval);
        }
      });
    }, 60000);

    return () => clearInterval(tokenCheckInterval);
  }, []);

  return (
    <AppStateContext.Provider
      value={{ state, dispatch, nextPane, cancelConnectivityTests, userAgentInfo, downloadFinalTestResults, startSTUNTest, startICETest }}
    >
      {children}
    </AppStateContext.Provider>
  );
};
